// src/pages/PrototypePage.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, User, Calendar, FileText, ClipboardCheck, Shield, Cpu, ArrowRight } from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';

const PrototypePage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    intendedUse: '',
    securityClearance: 'none',
    preferredDate: '',
    additionalNotes: ''
  });
  const navigate = useNavigate();
  
  // Simulated security clearance options
  const clearanceOptions = [
    { value: 'none', label: 'None' },
    { value: 'confidential', label: 'Confidential' },
    { value: 'secret', label: 'Secret' },
    { value: 'topsecret', label: 'Top Secret' },
    { value: 'tssci', label: 'TS/SCI' }
  ];
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Here you would normally send the data to your server
    console.log('Form submitted:', formData);
    
    // Simple validation first
    if (!formData.name || !formData.email || !formData.organization) {
      alert('Please fill in all required fields.');
      return;
    }
    
    // Show success message
    setFormSubmitted(true);
    
    // In a real app, you would make an API call here
    // For now, we'll just simulate a successful submission
  };
  
  // Tech grid background for visual consistency
  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-10">
        {[...Array(12)].map((_, i) => (
          <motion.div 
            key={i} 
            className="border-r border-gray-300/30 h-full"
            initial={{ opacity: 0, height: "0%" }}
            animate={{ 
              opacity: [0.2, 0.5, 0.2],
              height: "100%",
              borderColor: ["rgba(209, 213, 219, 0.1)", "rgba(209, 213, 219, 0.3)", "rgba(209, 213, 219, 0.1)"]
            }}
            transition={{ 
              opacity: { duration: 3, repeat: Infinity, ease: "easeInOut" },
              height: { duration: 1.5, delay: i * 0.1 },
              borderColor: { duration: 3, repeat: Infinity, ease: "easeInOut" }
            }}
          />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-10">
        {[...Array(12)].map((_, i) => (
          <motion.div 
            key={i} 
            className="border-b border-gray-300/30 w-full"
            initial={{ opacity: 0, width: "0%" }}
            animate={{ 
              opacity: [0.2, 0.5, 0.2],
              width: "100%",
              borderColor: ["rgba(209, 213, 219, 0.1)", "rgba(209, 213, 219, 0.3)", "rgba(209, 213, 219, 0.1)"]
            }}
            transition={{ 
              opacity: { duration: 3, repeat: Infinity, ease: "easeInOut" },
              width: { duration: 1.5, delay: i * 0.1 },
              borderColor: { duration: 3, repeat: Infinity, ease: "easeInOut" }
            }}
          />
        ))}
      </div>
    </div>
  );
  
  // Scanning animation effect
  const ScanEffect = () => (
    <motion.div 
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/5 to-transparent pointer-events-none"
      animate={{ 
        top: ["-100%", "100%"] 
      }}
      transition={{ 
        duration: 8, 
        repeat: Infinity, 
        ease: "linear" 
      }}
    />
  );

  return (
    <div className="relative min-h-screen bg-gray-950 text-gray-300 py-20">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-radial from-gray-900/10 via-gray-950 to-black z-0"></div>
      <ParticleBackground />
      <TechGrid />
      <ScanEffect />
      
      {/* Corner accents for visual consistency */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-gray-500/20 z-10" />
      <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-gray-500/20 z-10" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-gray-500/20 z-10" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-gray-500/20 z-10" />
      
      {/* Back button */}
      <motion.button
        onClick={() => navigate('/vanguard/capabilities')}
        className="fixed top-6 left-6 px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 
                   text-gray-300 rounded-md flex items-center shadow-lg shadow-black/30 border border-gray-700/50 z-20"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <ChevronLeft className="mr-2" size={16} />
        Back to Capabilities
      </motion.button>
      
      <div className="container mx-auto px-4 z-10 relative">
        <motion.div
          className="max-w-4xl mx-auto bg-gradient-to-b from-gray-900 to-gray-950 rounded-xl border border-gray-700/30 overflow-hidden shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {!formSubmitted ? (
            <>
              {/* Form header */}
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-8 py-6 border-b border-gray-700/30">
                <div className="flex items-center">
                  <div className="p-2 bg-gray-800 rounded-full mr-4">
                    <Shield className="h-8 w-8 text-gray-400" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-100">VANGUARD Prototype Access Request</h1>
                    <p className="text-gray-400 mt-1">Submit your information to request demonstration access to VANGUARD technology</p>
                  </div>
                </div>
              </div>
              
              {/* Form content */}
              <form onSubmit={handleSubmit} className="px-8 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left column */}
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
                        <User className="h-5 w-5 mr-2 text-gray-400" />
                        Personal Information
                      </h2>
                      
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-1">Name *</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                            placeholder="Enter your full name"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="organization" className="block text-sm font-medium text-gray-400 mb-1">Organization *</label>
                          <input
                            type="text"
                            id="organization"
                            name="organization"
                            value={formData.organization}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                            placeholder="Company or organization name"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1">Email *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                            placeholder="your@email.com"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                            placeholder="(000) 000-0000"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right column */}
                  <div className="space-y-6">
                    {/* Request Details */}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
                        <ClipboardCheck className="h-5 w-5 mr-2 text-gray-400" />
                        Request Details
                      </h2>
                      
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="intendedUse" className="block text-sm font-medium text-gray-400 mb-1">Intended Use *</label>
                          <select
                            id="intendedUse"
                            name="intendedUse"
                            value={formData.intendedUse}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                          >
                            <option value="">Select intended use</option>
                            <option value="military">Military Application</option>
                            <option value="government">Government Agency</option>
                            <option value="commercial">Commercial Use</option>
                            <option value="research">Research & Development</option>
                            <option value="partnership">Strategic Partnership</option>
                            <option value="investment">Investment Evaluation</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="securityClearance" className="block text-sm font-medium text-gray-400 mb-1">Security Clearance Level</label>
                          <select
                            id="securityClearance"
                            name="securityClearance"
                            value={formData.securityClearance}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                          >
                            {clearanceOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-400 mb-1">Preferred Demonstration Date</label>
                          <input
                            type="date"
                            id="preferredDate"
                            name="preferredDate"
                            value={formData.preferredDate}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-400 mb-1">Additional Notes</label>
                          <textarea
                            id="additionalNotes"
                            name="additionalNotes"
                            value={formData.additionalNotes}
                            onChange={handleInputChange}
                            rows="4"
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 text-gray-200"
                            placeholder="Any specific requirements or questions"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Security notice */}
                <div className="mt-8 p-4 bg-gray-800/50 border border-gray-700/50 rounded-md">
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-400">
                      <span className="font-semibold text-gray-300">Security Notice:</span> This form is protected by advanced encryption. Your information will be used solely for evaluating your request for demonstration access to VANGUARD technology. All access requests are subject to approval and may require additional verification.
                    </p>
                  </div>
                </div>
                
                {/* Submit button */}
                <div className="mt-8 flex justify-end">
                  <motion.button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 
                             text-gray-200 rounded-md flex items-center justify-center shadow-lg shadow-black/30 
                             border border-gray-600/30 font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Submit Request
                    <Send className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </form>
            </>
          ) : (
            // Success message
            <div className="px-8 py-10">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto bg-gray-800 rounded-full flex items-center justify-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  >
                    <Shield className="h-10 w-10 text-gray-400" />
                  </motion.div>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-100 mb-4">Request Submitted Successfully</h2>
                <p className="text-gray-400 max-w-xl mx-auto mb-8">
                  Thank you for your interest in VANGUARD technology. Your access request has been received and will be reviewed by our team. You will receive a confirmation email shortly with next steps.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    onClick={() => navigate('/vanguard')}
                    className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 
                             text-gray-300 rounded-md flex items-center justify-center shadow-lg shadow-black/30 
                             border border-gray-700/30"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Return to VANGUARD
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </motion.button>
                  
                  <motion.button
                    onClick={() => setFormSubmitted(false)}
                    className="px-6 py-3 bg-transparent border border-gray-700/50 hover:bg-gray-800/20 
                             text-gray-400 rounded-md flex items-center justify-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Submit Another Request
                  </motion.button>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
        
        {/* Footer note */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© 2025 RETICULI TECHNOLOGIES • STARKVILLE, MS</p>
          <p className="mt-1">VANGUARD is a product design by Reticuli Technologies</p>
        </div>
      </div>
    </div>
  );
};

export default PrototypePage;